import { App, Button, Progress, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FileUploadStatusEnum } from '../../../enums/FileUploadStatusEnum';
import { useAuth } from '../../../hooks/useAuth';
import useFilesActions from '../../../hooks/useFilesActions';
import { useFileUpload } from '../../../hooks/useFileUpload';
import useOpenFile from '../../../hooks/useOpenFile';
import type File from '../../../interfaces/File';
import { useAppStore } from '../../../store/store';
import { copyToClipboard, formatBytes } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import FileIcon from '../FileIcon';
import TruncatedTooltip from '../TruncatedTooltip';

/**
 * UploadProgressDrawer
 *
 * Displays a floating drawer UI that shows the progress of files currently being uploaded.
 * Supports minimizing, real-time speed/ETA calculation, clearing the upload queue, file-level actions, and error handling.
 */
const UploadProgressDrawer = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { authenticatedUser } = useAuth();
  const [isMinimized, setIsMinimized] = useState(false);

  // Hooks to access upload state and file actions
  const {
    uploadFilesQueue, // List of all files being uploaded (raw, not grouped by folder)
    isUploadingFiles, // Whether any files are currently uploading
    emptyUploadFilesQueue, // Function to clear upload queue
    cancelUploadItem, // Cancel a single file/folder upload (in queue or in progress)
    uploadGroupedFilesQueue, // Display list for grouped items (e.g. folders or single files)
    totalGroupedFilesAmount, // Total grouped amount for counter (files/folders)
    uploadedGroupedFilesAmount, // Amount done (for counter label)
    getFailedFilesInFolder, // function to get failed files in a grouped folder upload
    handleOpenUploadingFilesErrorsModal, // triggers modal showing upload errors
    isNetworkAvailable,
  } = useFileUpload();
  const { redirectFileToLocation } = useOpenFile();

  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({}); // for scrolling to current item
  const uploadHistoryRef = useRef<Record<string, { time: number; bytes: number }[]>>({}); // holds recent upload speed data per file
  const [prevUploadingFileId, setPrevUploadingFileId] = useState<string | null>(null);

  const params = useParams();

  // Sharing action (if owner of file)
  const { handleShareFiles } = useFilesActions({
    folderUid: params.folderUid,
  });

  /**
   * Calculates instantaneous upload speed for a specific file,
   * by using the bytes uploaded over the last 10 seconds.
   *
   * @param fileId The id of the file
   * @returns bytes per second, or null if not enough history
   */
  function getUploadSpeed(fileId: string): number | null {
    const history = uploadHistoryRef.current[fileId];
    if (!history || history.length < 2) return null;
    const first = history[0];
    const last = history[history.length - 1];
    if (!first || !last) return null;
    const deltaBytes = last.bytes - first.bytes;
    const deltaTime = (last.time - first.time) / 1000; // seconds
    if (deltaTime === 0) return null;
    return deltaBytes / deltaTime; // bytes/sec
  }

  /**
   * Calculates estimated time remaining (in seconds) for a file's upload.
   *
   * @param fileSize File size in bytes
   * @param uploaded Bytes uploaded so far
   * @param speed   Current bytes/sec (from getUploadSpeed)
   * @returns Seconds remaining, or null if not calculable
   */
  function getETA(fileSize: number, uploaded: number, speed: number | null): number | null {
    if (!speed || speed === 0) return null;
    return (fileSize - uploaded) / speed; // seconds
  }

  /**
   * Formats an ETA (in seconds) to a human-readable string.
   *
   * @param seconds
   * @returns e.g. "Mancano ~12s", "Mancano ~1 min 27s"
   */
  function formatETA(seconds: number | null): string {
    if (!seconds || !isFinite(seconds)) return '';
    if (seconds < 60) return `Left ~${Math.ceil(seconds)}s`;
    const min = Math.floor(seconds / 60);
    const sec = Math.ceil(seconds % 60);
    return `Left ~${min} min${sec > 0 ? ` ${sec}s` : ''}`;
  }

  /**
   * Automatically scroll the drawer list to the file currently uploading.
   * Only triggers if the uploading file changes.
   */
  useEffect(() => {
    const uploadingFile = uploadFilesQueue.find(
      (file) => file.status === FileUploadStatusEnum.Uploading,
    );

    // Only scroll if a new file started uploading and we have a ref for it
    if (
      uploadingFile &&
      itemRefs.current[uploadingFile.id] &&
      uploadingFile.id !== prevUploadingFileId
    ) {
      itemRefs?.current?.[uploadingFile.id]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setPrevUploadingFileId(uploadingFile.id);
    }
  }, [uploadFilesQueue]);

  /**
   * When uploads are reset (new upload starts), always maximize the drawer.
   * This ensures users don't miss new uploads if minimized.
   */
  useEffect(() => {
    if (isMinimized) {
      setIsMinimized(false);
    }
  }, [uploadGroupedFilesQueue.length]);

  /**
   * Every second, update each uploading file's bytes/history, for computing instantaneous upload speed and ETA.
   * Keeps only the last 10 seconds of history per file.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      uploadFilesQueue.forEach((file) => {
        if (
          file.status === FileUploadStatusEnum.Uploading &&
          file.file &&
          file.file.size &&
          file.progress !== undefined
        ) {
          const now = Date.now();
          const bytesUploaded = Math.round(file.file.size * (file.progress / 100));
          let history = uploadHistoryRef.current[file.id];
          if (!history) {
            history = [];
            uploadHistoryRef.current[file.id] = history;
          }
          history.push({ time: now, bytes: bytesUploaded });
          // Keep only 10 seconds worth of history
          uploadHistoryRef.current[file.id] = history.filter((e) => now - e.time < 10000);
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [uploadFilesQueue]);

  /**
   * Memoized title for the upload drawer, displaying a counter ("Uploaded" if all done, "Uploading" otherwise).
   * Depends on upload status and number of files uploaded vs total.
   */
  const title: string = useMemo(() => {
    const counterKey = !isUploadingFiles
      ? 'common.uploadedFilesCounter'
      : 'common.uploadingFilesCounter';
    const baseTitle = t(counterKey, {
      amount: uploadedGroupedFilesAmount,
      total: totalGroupedFilesAmount,
    });
    return !isNetworkAvailable ? `${baseTitle} - ${t('common.offline')}` : baseTitle;
  }, [
    isNetworkAvailable,
    isUploadingFiles,
    t,
    totalGroupedFilesAmount,
    uploadedGroupedFilesAmount,
  ]);

  // Hide drawer completely if there is nothing to show
  if (uploadGroupedFilesQueue.length === 0) {
    return null;
  }

  const toggleMinimized = (): void => {
    setIsMinimized((previous) => !previous);
  };

  return (
    <div
      id="upload-progress-drawer"
      role="region"
      aria-label={t('a11y.uploadProgress')}
      className={clsx(
        'border-gray-medium w-full rounded-md border bg-white transition-all duration-200 md:w-[400px] lg:w-[500px]',
        isMinimized ? 'h-12' : 'h-80',
      )}
    >
      {/* Header bar for title, minimize, and clear buttons */}
      <div
        className={clsx(
          'bg-gray-light flex items-center justify-between rounded-t-md border-b px-3 py-2',
          isMinimized ? 'border-transparent' : 'border-gray-medium',
        )}
      >
        <div className="text-blue-dark text-base font-medium">{title}</div>
        <div className="flex items-center">
          {/* Minimize/restore button */}
          <Button
            type="text"
            size="small"
            className="flex items-center pl-0"
            aria-label={
              isMinimized ? t('a11y.expandUploadProgress') : t('a11y.minimizeUploadProgress')
            }
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              toggleMinimized();
            }}
            icon={
              <Icon
                name="ChevronDownIcon"
                className={clsx(
                  'mt-1 h-5 w-5 transform transition-transform duration-200',
                  isMinimized ? 'rotate-180' : '',
                )}
              />
            }
          />
          {/* Empty queue button */}
          <Button
            type="text"
            size="small"
            onClick={() => emptyUploadFilesQueue()}
            disabled={uploadGroupedFilesQueue.length === 0}
            className="flex items-center pl-0"
            aria-label={t('a11y.clearUploadQueue')}
            icon={<Icon name="XIcon" className="mt-1 h-4 w-4" />}
          />
        </div>
      </div>

      {/* List of upload items */}
      <div className="divide-gray flex h-full max-h-fit flex-col divide-y overflow-y-auto px-3 pb-14">
        {uploadGroupedFilesQueue.map((file) => {
          const isWaitingForNetwork =
            file.status === FileUploadStatusEnum.Pending && !isNetworkAvailable;

          return (
            <div
              key={file.id}
              ref={(el) => {
                if (el) itemRefs.current[file.id] = el;
              }}
              className={clsx(
                'relative flex min-h-[35px] items-center justify-between transition-all duration-200',
                isWaitingForNetwork && 'bg-yellow-light',
              )}
            >
              {/* File icon and name */}
              <div className="flex items-center gap-1">
                <FileIcon
                  isFolder={file?.isFolder}
                  fileExtension={file.fileExtension ?? ''}
                  className="h-5 w-5"
                />
                <div className="flex flex-row items-center gap-2 truncate">
                  {(() => {
                    const isFileNameClickable =
                      file.status === FileUploadStatusEnum.Failed ||
                      file.status === FileUploadStatusEnum.Canceled ||
                      !!file.createdFile;
                    const fileNameClassName = clsx(
                      'max-w-60 truncate text-sm font-medium',
                      !!file.createdFile && 'cursor-pointer hover:underline',
                      file.status === FileUploadStatusEnum.Failed ||
                        file.status === FileUploadStatusEnum.Canceled
                        ? 'text-red-dark cursor-pointer hover:underline'
                        : isWaitingForNetwork
                          ? 'text-yellow-dark'
                          : 'text-blue-dark',
                    );
                    const handleFileNameClick = (): void => {
                      if (
                        file.status === FileUploadStatusEnum.Failed ||
                        file.status === FileUploadStatusEnum.Canceled
                      ) {
                        handleOpenUploadingFilesErrorsModal(false);
                        return;
                      }
                      if (file.createdFile) {
                        redirectFileToLocation({ file: file.createdFile as File });
                      }
                    };

                    if (!isFileNameClickable) {
                      return (
                        <span className={fileNameClassName}>
                          <TruncatedTooltip>{file.fileName}</TruncatedTooltip>
                        </span>
                      );
                    }

                    return (
                      <button
                        type="button"
                        aria-label={file.fileName}
                        onClick={handleFileNameClick}
                        className={clsx(fileNameClassName, 'border-0 bg-transparent p-0 text-left')}
                      >
                        <TruncatedTooltip>{file.fileName}</TruncatedTooltip>
                      </button>
                    );
                  })()}
                  {/* Show count & retries if this is a multi-file group */}
                  {file.totalFiles > 1 && (
                    <span className="text-blue-dark text-xs">
                      {file.uploadedFiles} of {file.totalFiles}
                    </span>
                  )}
                  {!!file?.retries && file?.retries > 0 && !file?.skipRetryDisplay && (
                    <span className="text-xs">
                      ({t('common.retries')}:{file.retries})
                    </span>
                  )}
                </div>
              </div>
              {/* Right section: progress details, actions, or error state */}
              <div className="flex items-center justify-center">
                {((): ReactNode => {
                  switch (file.status) {
                    case FileUploadStatusEnum.Pending:
                      return (
                        <div className="flex items-center gap-2">
                          <span
                            className={clsx(
                              'text-xs',
                              isWaitingForNetwork ? 'text-yellow-dark' : 'text-gray-dark',
                            )}
                          >
                            {!isNetworkAvailable
                              ? t('common.waitingForNetwork')
                              : t('common.waitingForUpload')}
                          </span>
                          <Spin size="small" />
                          <Tooltip title={t('common.cancelUpload')}>
                            <Button
                              type="text"
                              size="small"
                              icon={<Icon name="XIcon" className="h-4 w-4" />}
                              onClick={() => cancelUploadItem(file.id)}
                              aria-label={t('common.cancelUpload')}
                            />
                          </Tooltip>
                          {isWaitingForNetwork && (
                            <Icon name="UnplugIcon" className="text-yellow-dark h-5 w-5" />
                          )}
                        </div>
                      );
                    case FileUploadStatusEnum.Uploading:
                      // Actively uploading: show uploaded, speed & ETA
                      const uploaded = Math.round((file.fileSize * (file.progress ?? 0)) / 100);
                      const uploadSpeed = getUploadSpeed(file.id);
                      const eta = getETA(file.fileSize, uploaded, uploadSpeed);
                      return (
                        <div className="flex items-center gap-2">
                          <div className="text-gray-dark flex min-w-[160px] flex-col items-end text-xs">
                            <span>
                              {formatBytes(uploaded)} / {formatBytes(file.fileSize)}
                              {uploadSpeed && <> ({formatBytes(uploadSpeed)}/s)</>}
                            </span>
                            <span>{formatETA(eta)}</span>
                          </div>
                          <Tooltip title={t('common.cancelUpload')}>
                            <Button
                              type="text"
                              size="small"
                              icon={<Icon name="XIcon" className="h-4 w-4" />}
                              onClick={() => cancelUploadItem(file.id)}
                              aria-label={t('common.cancelUpload')}
                            />
                          </Tooltip>
                        </div>
                      );
                    case FileUploadStatusEnum.Completed:
                      // Upload completed: show share/copy link and success icon
                      return (
                        <div className="flex items-center">
                          {file.createdFile?.ownerId === authenticatedUser?.id ? (
                            // If user owns the file, show share button
                            <Tooltip placement="left" title={t('common.share')}>
                              <Button
                                aria-label={t('common.share')}
                                size="small"
                                type="text"
                                onClick={() => {
                                  handleShareFiles([file.createdFile as File], {
                                    onFilesCreated: () => {
                                      useAppStore.getState().events.emit.onFilesUpdated({
                                        files: [file.createdFile as File],
                                      });
                                    },
                                  });
                                }}
                              >
                                <Icon name="Share2Icon" className="text-blue-dark h-5 w-5" />
                              </Button>
                            </Tooltip>
                          ) : (
                            // Not owner: show copy link button
                            <Tooltip placement="left" title={t('common.copyLink')}>
                              <Button
                                aria-label={t('common.copyLink')}
                                className="px-2 py-0.5"
                                type="text"
                                size="small"
                                onClick={() => {
                                  copyToClipboard(
                                    `${window.location.origin}/file/${file?.createdFile?.id}`,
                                  )
                                    .then(() => {
                                      notification.success({
                                        title: t('notifications.linkCopiedToClipboard'),
                                      });
                                    })
                                    .catch(() => {
                                      notification.error({
                                        title: t('errors.hitASnag'),
                                      });
                                    });
                                }}
                              >
                                <Icon name="CopyIcon" className="text-blue-dark h-5 w-5" />
                              </Button>
                            </Tooltip>
                          )}
                          <Icon name="CircleCheckIcon" className="text-green-dark h-5 w-5" />
                        </div>
                      );
                    case FileUploadStatusEnum.Canceled:
                      // Canceled by user: red label, CircleSlash icon, clickable to open details (same pattern as Failed for multi-file folders)
                      return (
                        <div className="flex items-center">
                          {file.totalFiles > 1 &&
                          getFailedFilesInFolder(file.id).length < file.totalFiles ? (
                            <>
                              <Button
                                type="text"
                                size="small"
                                aria-label={t('a11y.viewUploadErrors')}
                                onClick={() => handleOpenUploadingFilesErrorsModal(false)}
                              >
                                <Icon name="CircleSlashIcon" className="text-red-dark h-5 w-5" />
                              </Button>
                              <Icon name="CircleCheckIcon" className="text-green-dark h-5 w-5" />
                            </>
                          ) : (
                            <Button
                              type="text"
                              size="small"
                              onClick={() => handleOpenUploadingFilesErrorsModal(false)}
                              className="flex items-center"
                            >
                              <div className="flex flex-row items-center gap-1">
                                <Icon name="CircleSlashIcon" className="text-red-dark h-5 w-5" />
                                <span className="text-red-dark text-sm">
                                  {t('common.canceled')}
                                </span>
                              </div>
                            </Button>
                          )}
                        </div>
                      );
                    case FileUploadStatusEnum.Failed:
                      // Show error(s): if a multi-file folder, allow error modal; otherwise, show failed
                      return (
                        <div className="flex items-center">
                          {file.totalFiles > 1 &&
                          getFailedFilesInFolder(file.id).length < file.totalFiles ? (
                            <>
                              <Button
                                type="text"
                                size="small"
                                aria-label={t('a11y.viewUploadErrors')}
                                onClick={() => {
                                  handleOpenUploadingFilesErrorsModal(false);
                                }}
                              >
                                <Icon name="TriangleAlertIcon" className="text-red-dark h-5 w-5" />
                              </Button>
                              <Icon name="CircleCheckIcon" className="text-green-dark h-5 w-5" />
                            </>
                          ) : (
                            <Button
                              type="text"
                              size="small"
                              onClick={() => {
                                handleOpenUploadingFilesErrorsModal(false);
                              }}
                            >
                              <div className="flex flex-row items-center gap-1">
                                <Icon name="CircleXIcon" className="text-red-dark h-5 w-5" />
                                <span className="text-red-dark text-sm">{t('common.failed')}</span>
                              </div>
                            </Button>
                          )}
                        </div>
                      );
                    default:
                      // Unknown/unhandled status
                      return 'N/D';
                  }
                })()}
              </div>
              {/* Progress bar: show for uploading, pending, failed, or canceled (not for complete) */}
              {[
                FileUploadStatusEnum.Uploading,
                FileUploadStatusEnum.Pending,
                FileUploadStatusEnum.Failed,
                FileUploadStatusEnum.Canceled,
              ].includes(file.status) && (
                <Progress
                  status={
                    file.status === FileUploadStatusEnum.Failed ||
                    file.status === FileUploadStatusEnum.Canceled
                      ? 'exception'
                      : 'active'
                  }
                  strokeColor={isWaitingForNetwork ? 'var(--color-yellow-dark)' : undefined}
                  className="absolute! right-0! bottom-0! left-0! z-10!"
                  size={{ height: 1 }}
                  percent={file.progress ?? 0}
                  showInfo={false}
                  style={{ height: '1px', minHeight: '1px' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UploadProgressDrawer;
